import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';
import { apiGet } from '../lib/api';
import type { PortfolioResponse } from '../../shared/types';
import { useCurrency, useTimeWindow } from '../lib/contexts';
import { deriveForScope, type Scope } from '../lib/scope';
import { TopBar } from '../components/TopBar';
import { HeroTotal } from '../components/HeroTotal';
import { BucketGrid } from '../components/BucketGrid';
import { HoldingsTable } from '../components/HoldingsTable';
import { AssetClassDonut } from '../components/AssetClassDonut';
import { GeographyDonut } from '../components/GeographyDonut';
import { RiskDistribution } from '../components/RiskDistribution';
import { NonTradableSection } from '../components/NonTradableSection';
import { HeroSkeleton, GridSkeleton } from '../components/Skeleton';
import './../styles/components/dashboard.css';

export function DashboardPage() {
  const [data, setData] = useState<PortfolioResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [scope, setScope] = useState<Scope>('liquid');
  const [bucketFilter, setBucketFilter] = useState<string | null>(null);
  const { ccy } = useCurrency();
  const { win } = useTimeWindow();

  const refetch = useCallback(() => {
    apiGet<PortfolioResponse>('/api/portfolio')
      .then(setData)
      .catch((e) => setError((e as Error).message));
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const view = useMemo(
    () => (data ? deriveForScope(data, scope) : null),
    [data, scope]
  );

  const allHoldings = useMemo(
    () => (view ? view.buckets.flatMap((b) => b.holdings) : []),
    [view]
  );

  if (error)
    return (
      <main class="dash">
        <p class="loss mono">Failed: {error}</p>
      </main>
    );
  if (!data || !view)
    return (
      <main class="dash">
        <TopBar pricesUpdatedAt={null} />
        <HeroSkeleton />
        <GridSkeleton />
      </main>
    );

  return (
    <main class="dash">
      <TopBar pricesUpdatedAt={data.prices_updated_at} />
      <HeroTotal
        totalUsd={view.totals.grand_total_usd}
        totalEur={view.totals.grand_total_eur}
        tradableUsd={view.totals.tradable_usd}
        tradableEur={view.totals.tradable_eur}
        weightedRisk={view.totals.weighted_risk}
        deltas={view.totals.deltas}
        pnl={view.totals.pnl}
        currencyExposure={view.currency_exposure}
        ccy={ccy}
        win={win}
        scope={scope}
        onScopeChange={setScope}
      />
      <BucketGrid
        buckets={view.buckets}
        ccy={ccy}
        win={win}
        activeBucket={bucketFilter}
        onBucketClick={(id) => setBucketFilter(bucketFilter === id ? null : id)}
      />
      {!bucketFilter && (
        <div class="dash-two-col">
          <div class="dash-panel-frame">
            <h3 class="panel-title">Asset Class</h3>
            <AssetClassDonut rollup={view.asset_classes} ccy={ccy} />
          </div>
          <div class="dash-panel-frame">
            <GeographyDonut rollup={view.geography} ccy={ccy} />
          </div>
        </div>
      )}
      {!bucketFilter && (
        <div class="dash-stack" style={{ marginBottom: 'var(--gap-section)' }}>
          <RiskDistribution points={view.risk_heatmap} ccy={ccy} />
        </div>
      )}
      <HoldingsTable
        rows={allHoldings}
        ccy={ccy}
        win={win}
        buckets={view.buckets}
        bucketFilter={bucketFilter}
        onBucketFilterChange={setBucketFilter}
        onHoldingSaved={refetch}
      />
      {scope === 'liquid' && !bucketFilter && (
        <NonTradableSection
          items={data.non_tradable}
          ccy={ccy}
          onSaved={refetch}
        />
      )}
      {data.warnings.length > 0 && (
        <div class="dash-warnings mono">
          {data.warnings.map((w, i) => (
            <div key={i} class="warn">
              ⚠ {w}
            </div>
          ))}
        </div>
      )}
    </main>
  );
}
